import React, { useMemo } from "react";
import { formatTime, formatWorkTime } from "../../salaries/salariesListGrid";
import {
    LinkToEmployeeWorkTimeDetails,
    ReportingDetailsTable,
} from "../employee/reportingDetailsTable";
import { StrMeasureUnit } from "../../../models/common/enums";
import { SalaryRowTypeItem } from "../../../models/salary/salaryRowTypeItem";
import { EmployeeListItemDto } from "../../../models/employee/employee";
import { ICostCenterItem } from "../../../models/costCenter/costCenterItem";
import { WorkTimeType } from "../../../models/workShitTimeSlot/workTimeType";
import { GroupedDataGridRow } from "../../framework/groupedDataGrid/groupedTreeDataGrid";
import { WorkTimeReportRowDto } from "../../../models/reporting/workTimeReport";
import { Base } from "../../../framework/base";
import { ICustomerItem } from "../../../models/customer/customerItem";
import { HourBookingCategory } from "../../../models/workTime/workHoursItem";
import { useTranslation } from "react-i18next";

export interface GridRow extends GroupedDataGridRow {
    rows?: WorkTimeReportRowDto[];
    salaryRowTypes: SalaryRowTypeItem[];
    employees: EmployeeListItemDto[];
    costCenters: ICostCenterItem[];
    workTimeTypes: WorkTimeType[];
    customers: ICustomerItem[];
}

interface WorkTimeDetailsProps {
    row: GridRow;
}

interface WorkTimeDetailsTableRow {
    rowId: string;
    employeeId: string;
    costCenterId: string | null;
    customerId: string | null;
    workTimeTypeId: string | null;
    dateStr: string;
    comment: string | null;
    startTime: string | null;
    endTime: string | null;
    salaryRowTypeId: string | null;
    category: HourBookingCategory;
    amount: number;
}

export const WorkTimeDetailsTable = (props: WorkTimeDetailsProps) => {
    const {
        rows,
        salaryRowTypes,
        employees,
        costCenters,
        workTimeTypes,
        customers,
    } = props.row;

    const { t } = useTranslation();

    const workHours: WorkTimeDetailsTableRow[] = useMemo(
        () =>
            rows?.flatMap(
                (
                    {
                        employeeId,
                        costCenterId,
                        workTimeTypeId,
                        customerId,
                        dateStr,
                        comment,
                        salaryRows,
                    },
                    i
                ) =>
                    salaryRows.map(
                        (
                            { salaryRowTypeId, amount, startTime, endTime, category },
                            j
                        ) => ({
                            employeeId,
                            costCenterId,
                            workTimeTypeId,
                            customerId,
                            dateStr,
                            comment,
                            startTime,
                            endTime,
                            salaryRowTypeId,
                            category,
                            amount,
                            rowId: `${i}.${j}`,
                        })
                    )
            ) ?? [],
        [rows]
    );

    const columns = useMemo(
        () => [
            {
                header: t("reporting.date"),
                renderCell: (row: WorkTimeDetailsTableRow) => (
                    <LinkToEmployeeWorkTimeDetails
                        employeeId={row.employeeId}
                        dateStr={row.dateStr}
                        content={Base.dayjsToDateStrWithWeekday(row.dateStr)}
                    />
                ),
            },
            {
                header: t("vehicle.startTime"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    !!row.startTime
                        ? formatTime(row.startTime, row.dateStr)
                        : "-",
            },
            {
                header: t("vehicle.endTime"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    !!row.endTime ? formatTime(row.endTime, row.dateStr) : "-",
            },
            {
                header: t("vehicle.employee"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    employees.find((e) => e.id === row?.employeeId)?.fullName ??
                    "-",
            },
            {
                header: t("vehicle.amount"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    formatWorkTime(
                        row?.amount,
                        salaryRowTypes.find((t) => t.id === row.salaryRowTypeId)
                            ?.measureUnit ?? StrMeasureUnit.HourFi,
                        2
                    ),
            },
            {
                header: t("vehicle.salaryRowType"),
                renderCell: (row: WorkTimeDetailsTableRow) => {
                    const srt = salaryRowTypes.find(
                        (srt) => srt.id === row?.salaryRowTypeId
                    );
                    return srt
                        ? `${srt.code} ${srt.name} (${srt.measureUnit})`
                        : t(`hourBookingCategory.${row.category}`);
                },
                colSpan: 2,
            },
            {
                header: t("reporting.customer"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    customers.find((c) => c?.id === row?.customerId)?.name ??
                    "-",
            },
            {
                header: t("reporting.costCenter"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    costCenters.find((c) => c?.id === row?.costCenterId)
                        ?.name ?? "-",
            },
            {
                header: t("workTime.workTimeType"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    workTimeTypes.find(
                        (wt) => wt?.workTimeTypeId === row?.workTimeTypeId
                    )?.name ?? "-",
            },
            {
                header: t("vehicle.comment"),
                renderCell: (row: WorkTimeDetailsTableRow) =>
                    row.comment || "-",
            },
        ],
        [costCenters, workTimeTypes, salaryRowTypes, employees, customers, t]
    );

    return <ReportingDetailsTable columns={columns} rows={workHours} />;
};